import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SEO } from '@/components/SEO';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Input } from '@/components/ui/input';
import { Book, Search, Code, Database, Shield, Zap, FileText, Play } from 'lucide-react';
import { useState } from 'react';
import logo from '@/assets/logo.png';

export default function Documentation() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const sections = [
    {
      icon: Book,
      title: "Getting Started",
      items: [
        { name: "Quick Start Guide", description: "Get up and running in 5 minutes" },
        { name: "Installation & Setup", description: "Detailed installation instructions" },
        { name: "Your First Reconciliation", description: "Step-by-step tutorial" },
        { name: "Understanding the Dashboard", description: "Navigate the interface" }
      ]
    },
    {
      icon: Database,
      title: "Integrations",
      items: [
        { name: "Tally ERP Integration", description: "XML import and sync guide" },
        { name: "QuickBooks Connection", description: "Connect your QuickBooks account" },
        { name: "Bank Statement Import", description: "CSV and PDF import formats" },
        { name: "API Integration", description: "Integrate with your systems" }
      ]
    },
    {
      icon: Code,
      title: "Features Guide",
      items: [
        { name: "AI Invoice Analysis", description: "How invoice scanning works" },
        { name: "Reconciliation Engine", description: "Understanding matching algorithms" },
        { name: "Fraud Detection", description: "Risk scoring and alerts" },
        { name: "Cash Flow Forecasting", description: "Predictive analytics guide" }
      ]
    },
    {
      icon: Shield,
      title: "Security",
      items: [
        { name: "Data Encryption", description: "How we protect your data" },
        { name: "Access Control", description: "User roles and permissions" },
        { name: "Compliance", description: "Regulatory standards" },
        { name: "Audit Logs", description: "Activity tracking" }
      ]
    }
  ];

  const quickLinks = [
    { icon: Zap, title: "Import Bank Transactions", code: "CSV format: Date, Amount, Narration, Reference" },
    { icon: FileText, title: "Upload Invoices", code: "Supports PDF, PNG, JPG. Max 10MB per file." },
    { icon: Play, title: "Run Reconciliation", code: "Click Sync > Auto-Match > Review Results" },
    { icon: Database, title: "Export Reports", code: "Dashboard > Reports > Export as PDF/Excel" }
  ];

  return (
    <>
      <SEO
        title="Documentation - Complete Guide | Sentinel"
        description="Comprehensive documentation for Sentinel. Learn how to set up integrations, use features, and maximize your financial reconciliation workflow."
      />
      
      <div className="min-h-screen bg-background">
        <nav className="border-b border-border/50 bg-background/95 backdrop-blur-xl sticky top-0 z-50">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
                <img src={logo} alt="SENTINEL Logo" className="w-10 h-10 brightness-0 dark:brightness-100 dark:invert" />
                <span className="text-2xl font-bold">SENTINEL</span>
              </div>
              <div className="flex items-center gap-4">
                <ThemeToggle />
                <Button onClick={() => navigate('/auth')}>Sign In</Button>
              </div>
            </div>
          </div>
        </nav>

        <div className="container mx-auto px-6 py-12 max-w-7xl">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold mb-4 pb-2">Documentation</h1>
            <p className="text-xl text-muted-foreground mb-8">
              Everything you need to master Sentinel
            </p>
            
            {/* Search */}
            <div className="max-w-2xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
              <Input
                placeholder="Search documentation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 text-lg"
              />
            </div>
          </div>

          {/* Quick Links */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold mb-6 pb-1">Quick Start</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {quickLinks.map((link, index) => (
                <Card key={index} className="p-6 hover:shadow-card-hover transition-all cursor-pointer border-border/50">
                  <link.icon className="w-8 h-8 text-primary mb-3" />
                  <h3 className="font-semibold mb-2">{link.title}</h3>
                  <p className="text-sm text-muted-foreground">{link.code}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Documentation Sections */}
          <div className="space-y-12">
            {sections.map((section, sectionIndex) => (
              <div key={sectionIndex}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center shadow-glow">
                    <section.icon className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold pb-1">{section.title}</h2>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {section.items.map((item, itemIndex) => (
                    <Card 
                      key={itemIndex} 
                      className="p-6 hover:shadow-card-hover transition-all cursor-pointer border-border/50 group"
                    >
                      <h3 className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
                        {item.name}
                      </h3>
                      <p className="text-muted-foreground">{item.description}</p>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Code Example */}
          <Card className="p-8 mt-16 border-primary/20 bg-surface">
            <h2 className="text-2xl font-bold mb-4 pb-1">Example: Import Transactions</h2>
            <pre className="bg-background p-6 rounded-lg overflow-x-auto">
              <code className="text-sm">
{`// Import bank transactions via API
import { supabase } from '@/integrations/supabase/client';

const importTransactions = async (transactions) => {
  const { data, error } = await supabase
    .from('bank_transactions')
    .insert(transactions.map(t => ({
      date: t.date,
      amount: t.amount,
      narration: t.description,
      bank_name: t.bank,
      status: 'unmatched'
    })));
    
  if (error) throw error;
  return data;
};`}
              </code>
            </pre>
          </Card>

          {/* Need Help */}
          <Card className="p-12 text-center mt-16 bg-gradient-primary border-0">
            <h2 className="text-4xl font-bold mb-4 pb-1 text-white">Need More Help?</h2>
            <p className="text-xl text-white/90 mb-8">
              Our support team is here to assist you
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" onClick={() => navigate('/support')}>
                Contact Support
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/api-reference')} className="text-white border-white hover:bg-white/10">
                View API Reference
              </Button>
            </div>
          </Card>
        </div>

        <footer className="border-t border-border/50 py-8 bg-surface/30 mt-20">
          <div className="container mx-auto px-6 text-center text-sm text-muted-foreground">
            <p>&copy; 2025 SENTINEL. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
}
